/**
 * Sztandary po drucie.
 *
 * **Format bierze się z serwera** (`/api/banners/spec`): wymiary, paleta i maski
 * kształtów leżą w `banners.rs` i nigdzie indziej. Obrazek gracza pobiera się
 * pod adresem z wersją, więc przeglądarka trzyma go bez końca, a sztandar
 * narysowany na nowo ma inny adres.
 */

const API = import.meta.env.VITE_API_URL ?? '';

export interface BannerShape {
  key: string;
  label: string;
  /** Wiersz po wierszu: 1 — pole należy do płata. */
  mask: number[];
}

export interface BannerSpec {
  width: number;
  height: number;
  palette: string[];
  shapes: BannerShape[];
}

export interface BannerDesign {
  shape: string;
  pixels: number[];
  /** Ród — opcjonalny przy zapisie, nadawany raz. */
  house?: string;
}

export interface Banner extends BannerDesign {
  version: number;
}

export interface Refused {
  code: string;
  message: string;
}

let spec: Promise<BannerSpec> | undefined;

/** Format sztandaru — raz na sesję. */
export function loadBannerSpec(): Promise<BannerSpec> {
  spec ??= fetch(`${API}/api/banners/spec`).then((response) => {
    if (!response.ok) throw new Error(`sztandary: ${response.status}`);
    return response.json() as Promise<BannerSpec>;
  });
  return spec;
}

const banners = new Map<string, Promise<Banner | null>>();

/** Sztandar gracza w tej wersji; `null`, gdy go nie ma. Każda para raz na sesję. */
export function loadBanner(player: string, version: number): Promise<Banner | null> {
  const key = `${player}@${version}`;
  let held = banners.get(key);
  if (!held) {
    held = fetch(`${API}/api/banners/${player}?v=${version}`)
      .then((response) => (response.ok ? (response.json() as Promise<Banner>) : null))
      .catch(() => null);
    banners.set(key, held);
  }
  return held;
}

/** Własny sztandar i to, czy wolno go mieć. */
export async function loadMyBanner(
  token: string
): Promise<{ allowed: boolean; banner: Banner | null }> {
  const response = await fetch(`${API}/api/banner`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error(`sztandar: ${response.status}`);
  return (await response.json()) as { allowed: boolean; banner: Banner | null };
}

/** Zapisz własny sztandar. Raz — drugi zapis serwer odrzuci. */
export async function createBanner(token: string, design: BannerDesign): Promise<Banner | Refused> {
  const response = await fetch(`${API}/api/banner`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(design),
  });
  const body = await response.json().catch(() => ({ code: 'broken', message: 'brak odpowiedzi' }));
  return response.ok ? (body as Banner) : (body as Refused);
}

/** Nadaj ród sztandarowi, który go jeszcze nie ma. Raz. */
export async function setHouse(token: string, house: string): Promise<{ house: string } | Refused> {
  const response = await fetch(`${API}/api/banner/house`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ house }),
  });
  const body = await response.json().catch(() => ({ code: 'broken', message: 'brak odpowiedzi' }));
  return response.ok ? (body as { house: string }) : (body as Refused);
}
